import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";

import DynamicTextInput from "../../components/Inputs/DynamicTextInput";

import {
  createOwnership,
  deleteOwnership,
  getOwnerships,
  updateOwnership,
} from "../../services/ownershipService";
import { ownershipSchema } from "../../validators/ownershipValidators";

const Ownership = () => {
  const [ownershipCategories, setOwnershipCategories] = useState([]);
  const [filteredOwnershipCategories, setFilteredOwnershipCategories] =
    useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ownershipCategoryToEdit, setOwnershipCategoryToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [ownershipCategoryToDelete, setownershipCategoryToDelete] =
    useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadOwnershipCategories();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = ownershipCategories.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOwnershipCategories(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, ownershipCategories]);

  const loadOwnershipCategories = async () => {
    setLoading(true);
    try {
      const { data } = await getOwnerships();
      setOwnershipCategories(data.data);
      setFilteredOwnershipCategories(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Ownership Categories"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (ownershipCategoryToEdit) {
      onUpdateOwnershipCategory(payload, resetForm);
    } else {
      const nonEmptyOwnershipCategories = payload?.ownershipCategories.filter(
        (ownershipCategory) => ownershipCategory?.name !== ""
      );

      if (nonEmptyOwnershipCategories.length <= 0) {
        toast.error("Ownership Categories cannot be empty!");

        return;
      }
      onCreateOwnershipCategories(nonEmptyOwnershipCategories, resetForm);
    }
  };

  const onCreateOwnershipCategories = async (
    ownershipCategoriesArray,
    resetForm
  ) => {
    setErrs(null);

    try {
      const promises = ownershipCategoriesArray.map(
        async (ownershipCategory) => {
          const payload = { name: ownershipCategory.name };

          const { data } = await createOwnership(payload);

          return data;
        }
      );

      const createdOwnershipCategories = await Promise.all(promises);

      setOwnershipCategories((prev) => [
        ...prev,
        ...createdOwnershipCategories,
      ]);

      toast.success("Ownership Category(s) created successfully");

      setIsModalOpen(false);

      resetForm({ values: { ownershipCategories: [{ name: "" }] } });

      await loadOwnershipCategories();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error ||
            "Some Ownership Categories were not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditingBody) => {
    setOwnershipCategoryToEdit(accreditingBody);
    setIsModalOpen(true);
  };

  const onUpdateOwnershipCategory = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalOwnershipCategories = [...ownershipCategories];

      const ownershipCategoryIndex = originalOwnershipCategories.findIndex(
        (ownershipCategory) =>
          ownershipCategory.id === ownershipCategoryToEdit.id
      );

      if (ownershipCategoryIndex === -1)
        throw new Error("Ownership Category not found.");

      const { data } = await updateOwnership(
        ownershipCategoryToEdit.id,
        payload
      );

      originalOwnershipCategories[ownershipCategoryIndex] = data.data;

      setOwnershipCategories(originalOwnershipCategories);

      setOwnershipCategoryToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Ownership Category updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Ownership Category not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setownershipCategoryToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteOwnershipCategory = async () => {
    const originalOwnershipCategories = [...ownershipCategories];

    const newOwnershipCategories = originalOwnershipCategories.filter(
      (ownershipCategory) =>
        ownershipCategory.id !== ownershipCategoryToDelete.id
    );

    setOwnershipCategories(newOwnershipCategories);

    try {
      await deleteOwnership(ownershipCategoryToDelete?.id);

      toast.success("Ownership Category deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Ownership Category not deleted, try again later"
        }`
      );

      setOwnershipCategories(originalOwnershipCategories);
    } finally {
      setIsDeleteModalOpen(false);

      setownershipCategoryToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setOwnershipCategoryToEdit(null);
    setownershipCategoryToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 500,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Ownership Category">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Ownership Category">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Ownership Categories"
        subtitle1="Institutions"
        subtitle2="Ownership Categories"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Ownership Categories List"
            count={filteredOwnershipCategories.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Ownership Category"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredOwnershipCategories.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {ownershipCategoryToEdit
                    ? "Edit Ownership Category"
                    : "Add Ownership Category"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            {ownershipCategoryToEdit ? (
              <Formik
                initialValues={{
                  name: ownershipCategoryToEdit
                    ? ownershipCategoryToEdit?.name
                    : "",
                }}
                enableReinitialize
                validationSchema={ownershipSchema}
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => {
                  return (
                    <Form className="space-y-5 px-4! py-4!">
                      {errs && <FormErrors errors={errs} />}

                      <TextInput
                        name="name"
                        title="Name"
                        placeholder="Enter Ownership Category"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        autoFocus
                      />

                      <div className="flex justify-end gap-2">
                        <FormCancelButton
                          text="Cancel"
                          onClick={handleCancel}
                        />
                        <FormButton
                          text="Save"
                          type="primary"
                          onClick={submitForm}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <Formik
                initialValues={{
                  ownershipCategories: [{ name: "" }],
                }}
                enableReinitialize
                onSubmit={(values, { resetForm }) => {
                  onSubmit(values, resetForm);
                }}
              >
                {({ values, handleChange, handleBlur, errors, submitForm }) => (
                  <Form className="space-y-2 !px-5 pb-3">
                    {errs && <FormErrors errors={errs} />}
                    <FieldArray name="ownershipCategories">
                      {({ remove, push }) =>
                        (values.ownershipCategories || []).map(
                          (accreditingBody, index) => (
                            <DynamicTextInput
                              key={index}
                              index={index}
                              name={`ownershipCategories.${index}.name`}
                              placeholder="Enter Ownership Category Name"
                              requiredField={true}
                              errors={errors}
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              push={push}
                              remove={remove}
                              pushName="ownershipCategories"
                              dynamicFieldMap={{
                                auditCategories: {
                                  name: "",
                                },
                              }}
                            />
                          )
                        )
                      }
                    </FieldArray>
                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteOwnershipCategory}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Ownership;
